// src/index.js - Updated with environment variable API URLs and improved error handling
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { store } from './store/store';

// Import components from their respective files
import MarsLoginForm from './component/Login';
import MarsSignupForm from './component/SignUp';
import Dashboard from './component/Dashboard';
import AffiliateLayout from './component/AffiliateRecruitments';
import AffiliateLayoutDirect from './component/AffiliateDirectory';
import SerpLayout from './component/SerpCheckers';
import KeywordLayout from './component/KeywordLayout';
import CompetitorSpyLayout from './component/competitorSpy/CompetitorSpyLayout';
// V2 gap page (same pipeline + country selector as competitor spy V2). Was built
// as affiliateGap/GapSpy but never routed; old AffiliateGapAnalyzer had no country field.
import AffiliateGapLayout from './component/affiliateGap/GapSpyLayout';
import ContactFinderLayout from './component/ContactFinderLayout';
import SocialMediaFinderLayout from './component/SocialMediaFinderLayout';
import ManualAffiliateAddLayout from './component/ManualAffiliateAddLayout';
import AffiliateAgentLayout from './component/AffiliateAgentLayout';
import PricingPage from './component/PricingPage';
import PricingPlanLayout from './component/PricingPlanLayout';
import ProfilePageLayout from './component/ProfilePageLayout';
import LiveAffiliateLayout from './component/liveAffiliate/liveAffiliateLayout';
import DocumentationLayout from './component/documentation/DocumentationLayout';
import ForgetPassword from './component/forgetpassword/forgetpassword';
import UntappedAffiliate from './component/untapped/untappedLayout';
import SmartTableLayout from './component/untapped/smartTableLayout';
import SentBox from './component/sentBox/sentBoxLayout';
import EmailSettingsLayout from './component/emailSetting/emailSettingLayout';
import AiApprovalLayout from './component/aiApproval/aiApprovalLayout';
import AffiliateRecruitmentToday from './component/affiliateRecruitmentoday';
import UpdateBrand from './component/updateBrand/updateBrandLayout';
import AssignBrands from './component/adminBrandAssign/AssignBrandsLayout';
import UserPanel from './component/userPanel/userPanelLayout';
import AdminSettingsLayout from './component/adminSettings/adminSettingsLayout';
import NotificationAffiliate from './component/notificationAffiliate/NotificatinAffiliateLayout';
// Import Payment Success Component
import PaymentSuccess from './component/PaymentSuccess';
import ExternalRedirect from './component/legal/ExternalRedirect';
import {
  TERMS_URL,
  PRIVACY_URL,
  PROSPECT_NOTICE_URL,
  DATA_REMOVAL_URL
} from './services/legal';
import PositionTracker from './component/positionTracker/PositionTrackerLayout';
import ManualAddBrand from './component/manualAdd/ManualAddBrand';
import BrandAdd from './component/brandAdd/BrandAdd';
import ManualDomainAddLayout from './component/manualDomainAdd/ManualDomainAddLayout';
import PaidTrafficLayout from './component/paidTraffic/PaidTrafficLayout';
import PaidTrafficLogLayout from './component/paidTraffic/PaidTrafficLogLayout';
import AddAffiliateLayout from './component/addAffiliate/AddAffiliateLayout';
import LiveAffiliateManageLayout from './component/liveAffiliateManage/LiveAffiliateManageLayout';
import AffiliateFinderLayout from './component/affiliateFinder/AffiliateFinderLayout';
import LiveAffiliateCheckerLayout from './component/liveAffiliateChecker/LiveAffiliateCheckerLayout';
import AffiliateFinderNoCostLayout from './component/affiliateFinderNoCost/AffiliateFinderNoCostLayout';
import TermsCheckerLayout from './component/termsChecker/TermsCheckerLayout';
import TermsCheckerV3Layout from './component/termsChecker/TermsCheckerV3Layout';
// Import standard Redux hooks
import { useSelector, useDispatch } from 'react-redux';
// Import auth actions from the correct path
import { loginSuccess, logout } from './store/slices/authSlice';
import SandBoxLayout from './component/sandBox/sandBoxLayout';
import CrmPipelineLayout from './component/crm/CrmPipelineLayout';
import KeywordGapLayout from './component/KeywordGapLayout';
import DebugConsoleLayout from './component/debugConsole/DebugConsoleLayout';
import ClassifyImportLayout from './component/classifyImport/ClassifyImportLayout';
import ReferralQueueLayout from './component/referralQueue/ReferralQueueLayout';
import AgentTeamLayout from './component/agentTeam/AgentTeamLayout';
import TrafficZeroFillLayout from './component/trafficZeroFill/TrafficZeroFillLayout';
import AffiliateSignup from './component/affiliatePortal/AffiliateSignup';
import AffiliatePortalLayout from './component/affiliatePortal/AffiliatePortalLayout';
import UserMessagesLayout from './component/messaging/UserMessagesLayout';
import ChatNotifications from './component/messaging/ChatNotifications';
import AffiliateEmailExportLayout from './component/affiliateEmailExport/AffiliateEmailExportLayout';
import TeamLayout from './component/team/TeamLayout';
import AcceptInvite from './component/team/AcceptInvite';
import { ChangePasswordRequired, AccessBlocked } from './component/team/AccessScreens';
import { primeAccount, pageBlockReason, featureForPath } from './services/account';
import { ThemeProvider } from './context/ThemeContext';

// ==================== API CONFIGURATION ====================
const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
};

// ==================== GENERAL SESSION PROTECTED ROUTE (USING ENV VARIABLE) ====================
const SessionProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [userRole, setUserRole] = useState(null);
  // Team account summary (role + permissions) from the same response; the
  // server enforces all of it, this only avoids showing a page that would 403.
  const [account, setAccount] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const verifySessionWithBackend = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const apiUrl = getApiUrl();

        // Check if there's a temp_token in URL (from email verification)
        const urlParams = new URLSearchParams(location.search);
        const tempToken = urlParams.get('temp_token');
        
        if (tempToken) {
          
          const tokenExchangeResponse = await fetch(`${apiUrl}/api/auth/exchange-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              tempToken: tempToken
            })
          });

          if (tokenExchangeResponse.ok) {
            const tokenData = await tokenExchangeResponse.json();
            
            // Remove temp_token from URL
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('temp_token');
            window.history.replaceState({}, '', newUrl);
            
          } else {
            console.error('❌ Token exchange failed:', tokenExchangeResponse.status);
            setError('Unable to verify your access. Please try logging in again.');
            setIsLoading(false);
            return;
          }
        }

        const response = await fetch(`${apiUrl}/api/auth/verify-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            timestamp: Date.now(),
            requestType: 'session_verification'
          })
        });


        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.hasSession) {
            setHasValidSession(true);
            setUserRole(data.user?.role || '');
            setAccount(data.account || null);
            primeAccount(data.account);
          } else {
            setHasValidSession(false);
            setError('Login timeout. Please sign in again to continue.');
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('❌ HTTP Error:', {
            status: response.status,
            statusText: response.statusText,
            errorData
          });
          setHasValidSession(false);
          
          if (response.status === 401) {
            setError('Login timeout. Please sign in again to continue.');
          } else if (response.status === 403) {
            setError('Access denied. Please check your account permissions.');
          } else if (response.status >= 500) {
            setError('Server is temporarily unavailable. Please try again in a few moments.');
          } else {
            setError('Unable to verify your session. Please try again.');
          }
        }
      } catch (error) {
        console.error('Session verification error:', error);
        setHasValidSession(false);
        
        // More user-friendly error messages
        let userMessage;
        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          userMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
        } else if (error.message.includes('NetworkError')) {
          userMessage = 'Network connection issue. Please try refreshing the page.';
        } else {
          userMessage = 'Something went wrong. Please try again or contact support if the issue persists.';
        }
        
        setError(userMessage);
      } finally {
        setIsLoading(false);
      }
    };

    verifySessionWithBackend();
  }, [location.search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying session...</p>
          <p className="mt-2 text-xs text-gray-400">Connecting to global affiliate network</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-red-600 mb-4 text-4xl">⏰</div>
            <h3 className="text-lg font-medium text-red-800 mb-3">Authentication Required</h3>
            <p className="text-red-700 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/login'}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Sign In Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
              >
                Try Again
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              If this problem continues, please contact our support team.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (hasValidSession) {
    // Affiliate accounts only get the messages portal — every other page
    // redirects there.
    if (userRole === 'affiliate') {
      return <Navigate to="/affiliate/messages" replace />;
    }
    // Team members: replace the emailed password first, and never open a page
    // their account admin has not given them.
    if (account?.mustChangePassword) {
      return <ChangePasswordRequired />;
    }
    const blocked = pageBlockReason(account, location.pathname);
    if (blocked) {
      return <AccessBlocked reason={blocked} featureLabel={featureForPath(account, location.pathname)?.label} />;
    }
    return children;
  }

  return <Navigate to="/login?error=session_required" replace />;
};

// ==================== ADMIN-ONLY ROUTE GUARD ====================
// Admin is identified by EMAIL everywhere in this product (users.role is not
// maintained), so this asks the server who the session belongs to rather than
// trusting anything client-side. This is UX, NOT the security boundary — every
// admin API also enforces the same email check server-side, which is what
// actually protects customer data.
const ADMIN_EMAIL = 'mars.mediacle@gmail.com';

const AdminOnly = ({ children }) => {
  const [access, setAccess] = useState('checking');

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/sessionValue`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        const data = await response.json().catch(() => ({}));
        const email = (data?.data?.businessEmail || '').trim().toLowerCase();
        if (!cancelled) setAccess(email === ADMIN_EMAIL ? 'allowed' : 'denied');
      } catch (err) {
        console.error('Admin check failed:', err);
        if (!cancelled) setAccess('denied');
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  if (access === 'checking') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (access === 'denied') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AdminProtectedRoute = ({ children }) => (
  <SessionProtectedRoute>
    <AdminOnly>{children}</AdminOnly>
  </SessionProtectedRoute>
);

// ==================== AFFILIATE PORTAL PROTECTED ROUTE ====================
// Guard for the affiliate-only portal. Affiliates are standalone accounts in
// the affiliate_users table with their own session, verified against
// /api/affiliate-auth/verify-session (NOT the main users-table session).
const AffiliateProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);

  useEffect(() => {
    const verifySession = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/api/affiliate-auth/verify-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.hasSession) {
            setHasValidSession(true);
          }
        }
      } catch (err) {
        console.error('Affiliate session verification error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    verifySession();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!hasValidSession) {
    return <Navigate to="/login?error=session_required" replace />;
  }
  return children;
};

// ==================== STANDARD PROTECTED ROUTE COMPONENT ====================
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  
  if (isAuthenticated && user?.emailVerified) {
    return children;
  }
  
  return <Navigate to="/login" replace />;
};

// ==================== PUBLIC ROUTE COMPONENT ====================
const PublicRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);

  if (isAuthenticated && user?.emailVerified) {
    return <Navigate to={user?.role === 'affiliate' ? '/affiliate/messages' : '/dashboard'} replace />;
  }

  return children;
};

// ==================== SESSION SYNC HOOK (USING ENV VARIABLE) ====================
const useSessionSync = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const syncSession = async () => {
    try {
      const apiUrl = getApiUrl();
      
      const response = await fetch(`${apiUrl}/api/auth/verify-session`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.hasSession && !isAuthenticated) {
          const userResponse = await fetch(`${apiUrl}/api/auth/me`, {
            method: 'GET',
            credentials: 'include'
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            if (userData.success) {
              dispatch(loginSuccess({
                user: userData.data
              }));
              return true;
            }
          }
        } else if (!data.hasSession && isAuthenticated) {
          dispatch(logout());
          return false;
        }
        
        return data.hasSession;
      }
    } catch (error) {
      console.error('Session sync error:', error);
      return false;
    }
  };

  return { syncSession };
};

// ==================== GLOBAL SESSION SYNC COMPONENT ====================
const GlobalSessionSync = () => {
  const { syncSession } = useSessionSync();
  
  useEffect(() => {
    syncSession();
    const interval = setInterval(syncSession, 5 * 60 * 1000);
    const handleFocus = () => syncSession();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [syncSession]);
  
  return null;
};

// ==================== SUCCESS MESSAGE COMPONENT ====================
const SuccessMessage = () => {
  const location = useLocation();
  const [showMessage, setShowMessage] = useState(false);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isVerified = urlParams.get('verified') === 'true';
    
    if (isVerified) {
      setShowMessage(true);
      const timer = setTimeout(() => setShowMessage(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [location]);
  
  if (!showMessage) return null;
  
  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 text-green-400">✅</div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Email Verified Successfully!
            </h3>
            <p className="mt-1 text-sm text-green-700">
              Welcome to MARS! You can now access your dashboard.
            </p>
          </div>
          <div className="ml-auto pl-3">
            <button
              onClick={() => setShowMessage(false)}
              className="text-green-400 hover:text-green-600"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PRICING PAGE WRAPPER ====================
const PricingPageWrapper = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const hasToken = urlParams.get('token');
  const hasEmail = urlParams.get('email');
  
  // If token and email are present, allow access without session
  if (hasToken && hasEmail) {
    return <PricingPage />;
  }
  
  // Otherwise, require session
  return (
    <SessionProtectedRoute>
      <PricingPage />
    </SessionProtectedRoute>
  );
};

// ==================== CHAT POPUPS FOR LOGGED-IN MARS USERS ====================
// Affiliates get theirs inside AffiliatePortalLayout (separate session type).
const UserChatNotifications = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  if (!isAuthenticated || !user?.emailVerified) return null;
  return <ChatNotifications role="user" />;
};

// ==================== MAIN APP COMPONENT ====================
const App = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);

  return (
    <>
      <GlobalSessionSync />
      <SuccessMessage />
      {/* The cookie banner stood here. It is gone because the thing it asked
          about is gone: this app sets strictly necessary cookies only (see the
          note in public/index.html). A banner is required again the moment any
          tag that is not needed to run the app comes back. */}
      {/* Affiliate chat popups for MARS users — temporarily hidden; uncomment to re-enable. */}
      {/* <UserChatNotifications /> */}
      <Routes>
        {/* Root route */}
        <Route
          path="/"
          element={
            (isAuthenticated && user?.emailVerified) ?
              <Navigate to={user?.role === 'affiliate' ? '/affiliate/messages' : '/dashboard'} replace /> :
              <Navigate to="/login" replace />
          }
        />
        
        {/* Login Route */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <MarsLoginForm />
            </PublicRoute>
          }
        />

        {/* Signup Route */}
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <MarsSignupForm />
            </PublicRoute>
          }
        />
        
        {/* Forgot password Route */}
        <Route 
          path="/reset-password" 
          element={
            <PublicRoute>
              <ForgetPassword />
            </PublicRoute>
          } 
        />

        {/* Every legal document, and the removal form, are published on
            mars.mediacle.com — the addresses printed inside the documents
            themselves. A fresh request for these paths is answered by nginx
            with a 301 and never gets here; these routes catch navigation that
            starts inside the running app. The two long-form paths are where
            the documents lived before. */}
        <Route path="/terms" element={<ExternalRedirect to={TERMS_URL} />} />
        <Route path="/privacy" element={<ExternalRedirect to={PRIVACY_URL} />} />
        <Route path="/terms-of-service" element={<ExternalRedirect to={TERMS_URL} />} />
        <Route path="/privacy-policy" element={<ExternalRedirect to={PRIVACY_URL} />} />
        <Route path="/prospect-data" element={<ExternalRedirect to={PROSPECT_NOTICE_URL} />} />
        <Route path="/data-removal" element={<ExternalRedirect to={DATA_REMOVAL_URL} />} />
        <Route path="/legal" element={<ExternalRedirect to="https://mars.mediacle.com/" />} />


        
        {/* Pricing Page - Public with token, protected without */}
        <Route 
          path="/pricing" 
          element={<PricingPageWrapper />}
        />

        {/* Payment Success Route */}
        <Route 
          path="/payment-success" 
          element={
            <SessionProtectedRoute>
              <PaymentSuccess />
            </SessionProtectedRoute>
          }
        />
        <Route
          path="/position-tracker"
          element={
            <SessionProtectedRoute>
              <PositionTracker />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/manualAdd"
          element={
            <SessionProtectedRoute>
              <ManualAddBrand />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/manual-domain-add"
          element={
            <SessionProtectedRoute>
              <ManualDomainAddLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/paid-traffic"
          element={
            <SessionProtectedRoute>
              <PaidTrafficLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/paid-traffic-log"
          element={
            <SessionProtectedRoute>
              <PaidTrafficLogLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/add-affiliate"
          element={
            <SessionProtectedRoute>
              <AddAffiliateLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/brand-add"
          element={
            <SessionProtectedRoute>
              <BrandAdd />
            </SessionProtectedRoute>
          }
        />

        {/* Protected Dashboard Route */}
        <Route 
          path="/dashboard" 
          element={
            <SessionProtectedRoute>
              <Dashboard />
            </SessionProtectedRoute>
          } 
        />
        
        {/* Feature Routes - Use SessionProtectedRoute for backend session verification */}
        <Route
          path="/affiliate-recruitment"
          element={
            <SessionProtectedRoute>
              <AffiliateLayout />
            </SessionProtectedRoute>
          }
        />

<Route 
          path="/SerpChecker" 
          element={
            <SessionProtectedRoute>
              <SerpLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route
          path="/affiliate-directory"
          element={
            <SessionProtectedRoute>
              <AffiliateLayoutDirect />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/affiliate-finder"
          element={
            <SessionProtectedRoute>
              <AffiliateFinderLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/live-affiliate-checker"
          element={
            <SessionProtectedRoute>
              <LiveAffiliateCheckerLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/affiliate-finder-nocost"
          element={
            <SessionProtectedRoute>
              <AffiliateFinderNoCostLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/terms-checker"
          element={
            <SessionProtectedRoute>
              <TermsCheckerLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/terms-checker-v3"
          element={
            <SessionProtectedRoute>
              <TermsCheckerV3Layout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/keyword-explorer" 
          element={
            <SessionProtectedRoute>
              <KeywordLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/competitor-spy" 
          element={
            <SessionProtectedRoute>
              <CompetitorSpyLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/live-affiliate" 
          element={
            <SessionProtectedRoute>
              <LiveAffiliateLayout />
            </SessionProtectedRoute>
          } 
        />

         <Route 
          path="/notification" 
          element={
            <SessionProtectedRoute>
              <NotificationAffiliate />
            </SessionProtectedRoute>
          } 
        />

         <Route
          path="/untapped-opportunities"
          element={
            <SessionProtectedRoute>
              <SmartTableLayout />
            </SessionProtectedRoute>
          }
        />

        {/* Smart Table is now the main untapped opportunities table */}
        {/* <Route
          path="/smart-table"
          element={
            <SessionProtectedRoute>
              <SmartTableLayout />
            </SessionProtectedRoute>
          }
        /> */}

          <Route
          path="/sent-box" 
          element={
            <SessionProtectedRoute>
              <SentBox />
            </SessionProtectedRoute>
          } 
        />
          <Route 
          path="/sand-box" 
          element={
            <SessionProtectedRoute>
              <SandBoxLayout />
            </SessionProtectedRoute>
          } 
        />
        
        <Route
          path="/email-settings"
          element={
            <SessionProtectedRoute>
              <EmailSettingsLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/ai-approval"
          element={
            <SessionProtectedRoute>
              <AiApprovalLayout />
            </SessionProtectedRoute>
          }
        />

        <Route 
          path="/profile" 
          element={
            <SessionProtectedRoute>
              <ProfilePageLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/documentation" 
          element={
            <SessionProtectedRoute>
              <DocumentationLayout />
            </SessionProtectedRoute>
          } 
        />

        <Route 
          path="/pricing-plan" 
          element={
            <SessionProtectedRoute>
              <PricingPlanLayout />
            </SessionProtectedRoute>
          } 
        />


         <Route
          path="/update-brand"
          element={
            <SessionProtectedRoute>
              <UpdateBrand />
            </SessionProtectedRoute>
          }
        />

         {/* Admin: hand any brand in the database to any account, as a project,
             a competitor, or one half of an affiliate gap. */}
         <Route
          path="/assign-brands"
          element={
            <AdminProtectedRoute>
              <AssignBrands />
            </AdminProtectedRoute>
          }
        />

         {/* V1 update: one long request over SSE, CSV required. Kept as the
             fallback while V2 (background job + polling) settles in. */}
         <Route
          path="/update-brand-v1"
          element={
            <SessionProtectedRoute>
              <UpdateBrand legacy />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/live-affiliate-manage"
          element={
            <AdminProtectedRoute>
              <LiveAffiliateManageLayout />
            </AdminProtectedRoute>
          }
        />

         <Route
          path="/user-panel"
          element={
            <AdminProtectedRoute>
              <UserPanel />
            </AdminProtectedRoute>
          }
        />

        {/* Team & Permissions — the account owner (and co-admins) manage the
            people on their account. The server gates /api/account the same way. */}
        <Route
          path="/settings/team"
          element={
            <SessionProtectedRoute>
              <TeamLayout />
            </SessionProtectedRoute>
          }
        />

        {/* Where the invite email's "Log in to MARS" button lands. Public: the
            person has no session yet, and an existing one is replaced. */}
        <Route path="/invite/:code" element={<AcceptInvite />} />

        {/* Admin: show/hide admin-only tooling in the UI (e.g. the referring-
            domains CSV picker on Affiliate Gap and Competitor Spy). */}
        <Route
          path="/admin-settings"
          element={
            <AdminProtectedRoute>
              <AdminSettingsLayout />
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/debug-console"
          element={
            <AdminProtectedRoute>
              <DebugConsoleLayout />
            </AdminProtectedRoute>
          }
        />

        {/* Admin: queue SEMrush CSVs to classify their domains ahead of any
            brand add. Fills the classification cache only. */}
        <Route
          path="/classify-queue"
          element={
            <AdminProtectedRoute>
              <ClassifyImportLayout />
            </AdminProtectedRoute>
          }
        />

        {/* Admin: referral domain + brand rows, each run through the add-brand
            pipeline into affiliate_clean. */}
        <Route
          path="/referral-queue"
          element={
            <AdminProtectedRoute>
              <ReferralQueueLayout />
            </AdminProtectedRoute>
          }
        />

        {/* The iGaming affiliate agent team (Scout, Vetter, Contact agent,
            Negotiator, Affiliate Manager), for every account: each one sees
            only its own teams, and nothing runs until someone presses
            "Start team". */}
        <Route
          path="/agent-team"
          element={
            <SessionProtectedRoute>
              <AgentTeamLayout />
            </SessionProtectedRoute>
          }
        />

        {/* Admin: replace zero traffic in affiliate_clean with a random 100–300. */}
        <Route
          path="/traffic-zero-fill"
          element={
            <AdminProtectedRoute>
              <TrafficZeroFillLayout />
            </AdminProtectedRoute>
          }
        />

        

         <Route 
          path="/affiliate-recruitment-today" 
          element={
            <SessionProtectedRoute>
              <AffiliateRecruitmentToday />
            </SessionProtectedRoute>
          } 
        />

        <Route
          path="/affiliate-gap"
          element={
            <SessionProtectedRoute>
              <AffiliateGapLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/keyword-gap"
          element={
            <SessionProtectedRoute>
              <KeywordGapLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/contact-finder"
          element={
            <SessionProtectedRoute>
              <ContactFinderLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/social-media-finder"
          element={
            <SessionProtectedRoute>
              <SocialMediaFinderLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/manual-affiliate-add"
          element={
            <SessionProtectedRoute>
              <ManualAffiliateAddLayout />
            </SessionProtectedRoute>
          }
        />

        <Route
          path="/affiliate-agent"
          element={
            <SessionProtectedRoute>
              <AffiliateAgentLayout />
            </SessionProtectedRoute>
          }
        />
        
        <Route
          path="/crm-pipeline"
          element={
            <SessionProtectedRoute>
              <CrmPipelineLayout />
            </SessionProtectedRoute>
          }
        />

        {/* Affiliate signup — temporarily hidden; uncomment to re-enable.
            (Public static link used in bulk invite emails; also handles the
            ?token=&email= verification return.) */}
        {/* <Route path="/affiliate-signup" element={<AffiliateSignup />} /> */}

        {/* Affiliates use the main login (it detects affiliate accounts and
            redirects to the portal); keep the old URL working as an alias. */}
        <Route path="/affiliate-login" element={<Navigate to="/login" replace />} />

        {/* Affiliate portal — messages only, affiliate accounts only */}
        <Route
          path="/affiliate/messages"
          element={
            <AffiliateProtectedRoute>
              <AffiliatePortalLayout />
            </AffiliateProtectedRoute>
          }
        />

        {/* MARS-user side of affiliate messaging — temporarily hidden; uncomment to re-enable. */}
        {/* <Route
          path="/messages"
          element={
            <SessionProtectedRoute>
              <UserMessagesLayout />
            </SessionProtectedRoute>
          }
        /> */}

        {/* Admin-only affiliate email export (backend enforces the gate) */}
        <Route
          path="/affiliate-email-export"
          element={
            <SessionProtectedRoute>
              <AffiliateEmailExportLayout />
            </SessionProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// ==================== ROOT RENDER ====================
const root = ReactDOM.createRoot(document.getElementById('root'));
// Google OAuth Client ID
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '271320243979-dt51m782dkpee5fsd967vfinbp8bicn5.apps.googleusercontent.com';

root.render(
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Provider store={store}>
        <ThemeProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ThemeProvider>
      </Provider>
    </GoogleOAuthProvider>
);

reportWebVitals();

// ==================== EXPORTS ====================
export { 
  SessionProtectedRoute, 
  ProtectedRoute, 
  PublicRoute, 
  useSessionSync 
};